import { CustomerProductName } from 'src/constants/customer/enums'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { isCustomerProductActive } from 'src/utils/customerProductList'
import type { State } from '../reducers/types'

/**
 * Returns the stored array by reference. Never build a new array or object literal in a
 * selector — every consuming `useSelector` would re-render on each dispatch. Filtering and
 * sorting belong in the component under `useMemo`.
 */
export const getCustomerProducts = (state: State): CustomerProduct[] => {
  return state.customerProduct.products
}

export const getCustomerProductsLoading = (state: State): boolean => {
  return state.customerProduct.loading
}

export const getCustomerProductsError = (state: State): string | null => {
  return state.customerProduct.error
}

export const getCustomerProductSaving = (state: State): boolean => {
  return state.customerProduct.saving
}

/** The customer a products request was last made for — see the design doc's §4.1. */
export const getCustomerProductsPartnerId = (state: State): string | null => {
  return state.customerProduct.partnerId
}

/**
 * `CustomerLayout`'s settled marker: the customer whose products have actually succeeded or
 * failed. The layout gates the tab outlet on this rather than on `loading`, so a same-customer
 * refetch — a manual refresh, a post-mutation refetch, or a background token refresh — cannot
 * unmount the tab subtree.
 */
export const getCustomerProductsLoadedPartnerId = (state: State): string | null => {
  return state.customerProduct.loadedPartnerId
}

/**
 * The `api_integration` feature gate, replacing `selectors/apiIntegration.ts`. Keeps the old
 * name and signature so `SendTestWebhookFields` needs only an import change.
 *
 * Returns a primitive, so `useSelector`'s default `===` comparison is stable.
 *
 * Deliberately does NOT re-check `partnerId`: `CustomerLayout` has already withheld every
 * consumer until the slice matches the route (§4.1), so a check here would be dead code
 * implying a protection a plain selector cannot actually provide.
 */
export const getHasApiIntegration = (state: State): boolean => {
  return state.customerProduct.products.some((product) => {
    // Name alone is not enough. The fetch returns future-dated rows too, while the backend's
    // send-webhook precondition calls `PartnerProductService.getActive`, which requires
    // `startTime <= now` — so a product starting next month would open this gate and then be
    // refused with 400 'Partner does not have api_integration product enabled'.
    return (
      product.product === CustomerProductName.ApiIntegration && isCustomerProductActive(product)
    )
  })
}

export const getApiIntegrationLoading = (state: State): boolean => {
  return state.customerProduct.loading
}
